import { ITableColumn, TextAlign } from '@busybusy/webapp-react-ui';
import OptionallyFormattedHours from 'components/foundation/text/OptionallyFormattedHours/OptionallyFormattedHours';
import { useCurrencyNumberFormatter, useReduxSelector } from 'hooks';
import { isNumber, keyBy, sortBy, sumBy } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { KeysOfType } from 'types/util/KeysOfType';
import { Optional } from 'types/util/Optional';
import { t } from 'utils/localize';
import { IEquipmentActivityTableRowInfo } from './useEquipmentActivity';

export default function useEquipmentActivityReportDetailTable<RowType extends IEquipmentActivityTableRowInfo>(
  data: RowType[],
  nameColumnWidth: number = 300
) {
  const formatter = useCurrencyNumberFormatter('usd', { maximumFractionDigits: 2 })!;
  const availableColumns = useReduxSelector((state) => state.activityReports.equipmentTableColumns);
  const columnsMap = useMemo(() => keyBy(availableColumns, 'key'), [availableColumns]);

  const renderReportTimeValue = (row: RowType, col: ITableColumn<RowType>) => {
    let answer: number | null = null;
    switch (col.key) {
      case 'regularHours':
      case 'regularHoursDec':
        answer = row.regularHours;
        break;
      case 'overtimeHours':
      case 'overtimeHoursDec':
        answer = row.overtimeHours;
        break;
      case 'doubleTimeHours':
      case 'doubleTimeHoursDec':
        answer = row.doubleTimeHours;
        break;
      case 'totalHours':
      case 'totalHoursDec':
        answer = row.totalHours;
        break;
    }

    const isDecimal = col.key.includes('Dec');

    return <OptionallyFormattedHours key={row.id} value={answer} payload={{ type: isDecimal ? 'DECIMAL' : 'TIME' }} />;
  };

  const renderReportCostValue = (row: RowType, col: ITableColumn<RowType>) => {
    let answer: number | null = null;

    const rowValue = row[col.key as keyof IEquipmentActivityTableRowInfo] ?? null;

    if (isNumber(rowValue) || rowValue === null) {
      answer = rowValue;
    }

    return answer ? formatter.format(answer) : '---';
  };

  function renderColumnTimeFooter(col: ITableColumn<RowType>): ReactNode {
    let answer: number | null = null;

    if (data) {
      switch (col.key) {
        case 'regularHours':
        case 'regularHoursDec':
          answer = sumBy(data, (entry) => entry.regularHours ?? 0);
          break;
        case 'overtimeHours':
        case 'overtimeHoursDec':
          answer = sumBy(data, (entry) => entry.overtimeHours ?? 0);
          break;
        case 'doubleTimeHours':
        case 'doubleTimeHoursDec':
          answer = sumBy(data, (entry) => entry.doubleTimeHours ?? 0);
          break;
        case 'totalHours':
        case 'totalHoursDec':
          answer = sumBy(data, (entry) => entry.totalHours ?? 0);
          break;
      }
    }

    const isDecimal = col.key.includes('Dec');

    return <OptionallyFormattedHours payload={{ type: isDecimal ? 'DECIMAL' : 'TIME' }} value={answer} />;
  }

  function renderColumnCostFooterCurried(key: KeysOfType<IEquipmentActivityTableRowInfo, Optional<number>>) {
    return () => {
      const answer = sumBy(data, key);
      return answer !== 0 ? formatter.format(answer) : '---';
    };
  }

  const aggregateColumns = useMemo(() => {
    const columns = [
      {
        key: 'regularHours',
        title: t('Reg Hrs'),
        sort: true,
        align: TextAlign.RIGHT,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'regularHoursDec',
        title: t('Reg Hrs (Decimal)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'overtimeHours',
        title: t('OT Hrs'),
        sort: true,
        align: TextAlign.RIGHT,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'overtimeHoursDec',
        title: t('OT Hrs (Decimal)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'doubleTimeHours',
        title: t('DT Hrs'),
        sort: true,
        align: TextAlign.RIGHT,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'doubleTimeHoursDec',
        title: t('DT Hrs (Decimal)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'totalHours',
        title: t('Total Hrs'),
        sort: true,
        align: TextAlign.RIGHT,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'totalHoursDec',
        title: t('Total Hrs (Decimal)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportTimeValue,
        footer: renderColumnTimeFooter,
      },
      {
        key: 'regularCost',
        title: t('Reg Cost (Labor)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('regularCost'),
      },
      {
        key: 'overtimeCost',
        title: t('OT Cost (Labor)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('overtimeCost'),
      },
      {
        key: 'doubletimeCost',
        title: t('DT Cost (Labor)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('doubletimeCost'),
      },
      {
        key: 'totalCost',
        title: t('Total Cost (Labor)'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('totalCost'),
      },
      {
        key: 'laborBurden',
        title: t('Labor Burden'),
        sort: true,
        align: TextAlign.RIGHT,
        headerTextWrap: true,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('laborBurden'),
      },
      {
        key: 'equipmentRate',
        title: t('Equip. Rate'),
        sort: true,
        align: TextAlign.RIGHT,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('equipmentRate'),
      },
      {
        key: 'equipmentCost',
        title: t('Equip. Cost'),
        sort: true,
        align: TextAlign.RIGHT,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('equipmentCost'),
      },
      {
        key: 'equipmentTotalCost',
        title: t('Total Cost'),
        sort: true,
        align: TextAlign.RIGHT,
        cell: renderReportCostValue,
        footer: renderColumnCostFooterCurried('equipmentTotalCost'),
      },
    ];

    return sortBy(
      columns.filter((col) => columnsMap[col.key]?.visible === true),
      (col) => columnsMap[col.key]?.position ?? 0
    );
  }, [columnsMap, data]);

  const tableMinWidth = useMemo(
    () => aggregateColumns.length * 140 + nameColumnWidth,
    [aggregateColumns, nameColumnWidth]
  );

  return {
    aggregateColumns,
    tableMinWidth,
  };
}
